import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineStyle } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import { useMarketContext } from '../context/MarketDataContext';
import {
  SESSION_TF, SessionBandPrimitive, buildSessionBands,
  DayLinePrimitive, buildDayLines,
  baseChartOptions, TfButtons, SessionLegend, attachVerticalPan,
} from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

const PIVOT_LINE_STYLE = {
  solid: LineStyle.Solid,
  dashed: LineStyle.Dashed,
};

// 2026-07-15: demoted from the primary refresh mechanism to a backstop -
// the still-forming candle now redraws live off underlying_trade WS ticks
// (see applyLivePrice below), same mechanism ContractChartWidget already
// uses. This poll's only job now is picking up the just-closed bar (which
// the tick-built candle only approximates) and self-healing a missed tick.
const POLL_MS = {
  '1m': 60000, '5m': 120000, '15m': 300000,
  '30m': 600000, '1h': 900000, '4h': 1200000, '1D': 1800000,
};

// How many seconds each timeframe bar covers - used to compute the current
// bar's start time for the tick-driven live candle. Same convention as
// ContractChartWidget's TF_SECONDS.
const TF_SECONDS = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1D': 86400,
};

// How long since the last received tick before the live indicator stops
// calling it "Live" - generously above the 250ms server-side throttle so a
// brief lull between trades on a less-liquid name doesn't flicker the badge.
const TICK_STALE_MS = 8000;

export default function ChartWidget({ tf, setTf, symbol = 'SPY', setSymbol, tickers = ['SPY'] }) {
  const { underlyingTicks, connected: wsConnected } = useMarketContext();
  const [showPivots, setShowPivots] = useState(true);
  const [, setClockTick] = useState(0); // forces a re-render each second so the tick-age badge stays current
  const lastTickAtRef = useRef(null);
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  const primitiveRef = useRef(null);
  const dayLineRef   = useRef(null);
  const pollRef        = useRef(null);
  const mountedRef     = useRef(true);
  const initialDone    = useRef(false);
  const resetVPanRef   = useRef(null);
  const pivotLinesRef  = useRef([]);
  const liveBarRef     = useRef(null);
  const latestPriceRef = useRef(null);

  // SPX has no direct spot feed - the backend subscribes "SPY" ticks for it
  // (see backend/handlers/chart_bars.go's fetchSym mapping). Prefers the
  // real-open-anchored formula (backend/handlers/spx_price.go - validated
  // ~5x more accurate) whenever today's open has been entered, matching what
  // the REST payload already computes server-side; falls back to the old
  // SPY×10+35 proxy otherwise.
  const isSpx = symbol === 'SPX';
  const tickSym = isSpx ? 'SPY' : symbol;
  const rawTick = underlyingTicks[tickSym];
  const [spxOpen, setSpxOpen] = useState(null); // { open_price, spy_open, found }
  useEffect(() => {
    if (!isSpx) return;
    let mounted = true;
    (async () => {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/spx-open`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (mounted) setSpxOpen(await res.json());
      } catch {
        if (mounted) setSpxOpen(null);
      }
    })();
    return () => { mounted = false; };
  }, [isSpx]);
  const livePrice = rawTick == null ? null : !isSpx ? rawTick
    : (spxOpen?.found && spxOpen.spy_open > 0)
      ? spxOpen.open_price * (rawTick / spxOpen.spy_open)
      : rawTick * 10 + 35;

  // Push the live underlying price as a synthetic partial bar onto the
  // chart, redrawing the still-forming candle in place - same pattern as
  // ContractChartWidget's applyLivePrice. Safe to call before initialDone -
  // returns early if the chart isn't ready yet.
  const applyLivePrice = (price) => {
    if (!seriesRef.current || !price || !initialDone.current) return;
    const barSec = TF_SECONDS[tf] || 60;
    const nowSec = Math.floor(Date.now() / 1000);
    const barStart = Math.floor(nowSec / barSec) * barSec;
    const prev = liveBarRef.current;
    const bar = {
      time:  barStart,
      open:  (prev?.time === barStart) ? prev.open                 : price,
      high:  (prev?.time === barStart) ? Math.max(prev.high, price) : price,
      low:   (prev?.time === barStart) ? Math.min(prev.low,  price) : price,
      close: price,
    };
    liveBarRef.current = bar;
    try { seriesRef.current.update(bar); } catch { /* chart not ready */ }
  };

  // Create chart once on mount
  useEffect(() => {
    mountedRef.current = true;

    const chart = createChart(containerRef.current, baseChartOptions(containerRef.current.clientWidth || 300));
    chartRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderUpColor: '#26a69a', borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    series.attachPrimitive(primitive);

    // Attach day-line primitive AFTER session bands so it renders on top of them.
    const dayLine = new DayLinePrimitive();
    dayLineRef.current = dayLine;
    series.attachPrimitive(dayLine);

    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    const { detach: detachVPan, reset } = attachVerticalPan(containerRef.current, [series]);
    resetVPanRef.current = reset;

    return () => {
      mountedRef.current = false;
      detachVPan();
      resetVPanRef.current = null;
      ro.disconnect();
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = seriesRef.current = primitiveRef.current = dayLineRef.current = null;
    };
  }, []);

  // Refetch when timeframe or symbol changes
  useEffect(() => {
    initialDone.current = false;
    liveBarRef.current = null;
    clearInterval(pollRef.current);

    resetVPanRef.current?.();
    seriesRef.current?.setData([]);
    primitiveRef.current?.clearBands();
    dayLineRef.current?.clearLines();

    const fetchBars = async (isInitial) => {
      if (!mountedRef.current || !seriesRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(`${API_URL}/api/chart/bars?symbol=${symbol}&tf=${tf}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mountedRef.current) return;

        const bars = await res.json();
        if (!bars || !bars.length || !mountedRef.current) return;

        if (isInitial || !initialDone.current) {
          resetVPanRef.current?.();
          seriesRef.current.applyOptions({ autoscaleInfoProvider: (getDefault) => getDefault() });
          seriesRef.current.setData(bars);
          chartRef.current.priceScale('right').applyOptions({ autoScale: true });
          chartRef.current.timeScale().fitContent();
          initialDone.current = true;
        } else {
          seriesRef.current.update(bars[bars.length - 1]);
        }

        if (SESSION_TF.has(tf)) {
          primitiveRef.current?.setBands(buildSessionBands(bars));
        } else {
          primitiveRef.current?.clearBands();
        }

        dayLineRef.current?.setLines(buildDayLines(bars));

        // Always re-apply the live price on top of the fetched bars so the
        // current partial bar stays visible (the bars API only returns
        // completed bars) - same convention as ContractChartWidget.
        if (latestPriceRef.current != null) {
          applyLivePrice(latestPriceRef.current);
        }
      } catch { /* stale data shown until next poll */ }
    };

    fetchBars(true);
    pollRef.current = setInterval(() => fetchBars(false), POLL_MS[tf] || 60000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tf, symbol]);

  // Redraw the live candle the instant a fresh underlying tick arrives over
  // WS - this is what makes the still-forming bar update tick-by-tick
  // instead of only on the POLL_MS backstop.
  useEffect(() => {
    if (livePrice != null) {
      latestPriceRef.current = livePrice;
      lastTickAtRef.current = Date.now();
      applyLivePrice(livePrice);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePrice]);

  // Keep the forming candle rolling over bar boundaries on time, not just
  // when a tick happens to land - ticks stop arriving between trades, but a
  // new bar period should still start right at its boundary. Same reasoning
  // as ContractChartWidget's identical 1s ticker.
  useEffect(() => {
    const id = setInterval(() => {
      if (latestPriceRef.current != null) applyLivePrice(latestPriceRef.current);
      setClockTick(c => c + 1); // re-render so the tick-live badge's age check stays fresh
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tf]);

  // Reset tick-live tracking when the charted symbol changes - a switch to a
  // new ticker shouldn't show "Live" off the previous symbol's last tick.
  useEffect(() => {
    lastTickAtRef.current = null;
  }, [symbol]);

  // Fetch and draw pivot levels when symbol or toggle changes
  useEffect(() => {
    if (!showPivots || !seriesRef.current) {
      // Remove existing lines
      for (const pl of pivotLinesRef.current) {
        try { seriesRef.current?.removePriceLine(pl); } catch {}
      }
      pivotLinesRef.current = [];
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await getValidToken();
        if (!token || cancelled) return;
        const res = await fetch(`${API_URL}/api/chart/pivot-levels?symbol=${symbol}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || !seriesRef.current) return;

        // Remove old lines
        for (const pl of pivotLinesRef.current) {
          try { seriesRef.current.removePriceLine(pl); } catch {}
        }
        pivotLinesRef.current = [];

        // Draw new lines
        for (const lv of (data.levels || [])) {
          const pl = seriesRef.current.createPriceLine({
            price: lv.price,
            color: lv.color,
            lineWidth: 1,
            lineStyle: PIVOT_LINE_STYLE[lv.style] ?? LineStyle.Solid,
            axisLabelVisible: true,
            title: lv.label,
          });
          pivotLinesRef.current.push(pl);
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
      for (const pl of pivotLinesRef.current) {
        try { seriesRef.current?.removePriceLine(pl); } catch {}
      }
      pivotLinesRef.current = [];
    };
  }, [symbol, showPivots]);

  const tickAgeMs = lastTickAtRef.current != null ? Date.now() - lastTickAtRef.current : Infinity;
  const isTickLive = wsConnected && tickAgeMs < TICK_STALE_MS;
  const tickDotColor = !wsConnected ? '#ef4444' : isTickLive ? '#22c55e' : '#6b7280';
  const tickLabel = !wsConnected ? 'WS disconnected' : isTickLive ? 'Live tick' : 'No ticks';

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-3 flex-wrap">

        {/* Symbol dropdown */}
        <div className="relative">
          <select
            value={symbol}
            onChange={e => setSymbol?.(e.target.value)}
            className="appearance-none text-xs font-bold rounded px-3 py-1.5 pr-7 border cursor-pointer focus:outline-none focus:border-blue-500"
            style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
          >
            {tickers.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
        </div>

        {/* Live price, sourced from the same tick stream driving the candle */}
        {livePrice != null && (
          <span className="text-sm font-bold tabular-nums" style={{ color: '#60a5fa' }}>
            ${livePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        )}

        {/* Tick-live indicator - confirms the candle is being driven by real
            WS ticks, not just the POLL_MS backstop timer */}
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded"
          style={{ backgroundColor: tickDotColor + '22', color: tickDotColor }}
          title={isTickLive ? 'Receiving live trade ticks for this symbol' : !wsConnected ? 'WebSocket connection is down' : 'No ticks received recently - market may be closed for this symbol'}
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${isTickLive ? 'animate-pulse' : ''}`}
            style={{ backgroundColor: tickDotColor }}
          />
          {tickLabel}
        </span>

        {/* SPX note — which proxy formula is currently backing the price */}
        {isSpx && (
          <span className="text-xs text-gray-500">
            {spxOpen?.found ? 'SPX open-anchored' : '~SPY×10+35 (no open set)'}
          </span>
        )}

        <TfButtons tf={tf} setTf={setTf} />
        {SESSION_TF.has(tf) && <SessionLegend />}

        <div className="flex-1" />

        <button
          onClick={() => setShowPivots(p => !p)}
          className="text-xs font-medium px-2 py-1 rounded transition-colors"
          style={
            showPivots
              ? { backgroundColor: '#3b82f622', color: '#3b82f6', border: '1px solid #3b82f655' }
              : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid transparent' }
          }
        >Pivots</button>
      </div>
      <div ref={containerRef} />
    </div>
  );
}
